import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { FaArrowLeft, FaExclamationTriangle, FaFileAlt, FaFileInvoice, FaHistory, FaListUl, FaMoneyCheckAlt, FaSync, FaThumbsUp, FaCalendarAlt } from 'react-icons/fa';
import ErpTemplate from '../../../../components/ErpTemplate';
import PagamentoLegadoParcela from '../../../../components/Financeiro/PagamentoLegadoParcela';
import api from '../../../../services/api';
import { moeda } from '../../../../util/util';
import { formatarValorMoeda } from '../../../../components/moedaOrigemPolicy';
import { agruparItensPorGrupo } from '../../../Orcamento/itensFornecedorPolicy';
import { labelFormaPgto, listaDadosPagamento } from './comoPagarPolicy';
import { itensDoPlano, situacaoPlano, situacaoNfPlano, rotaDetalhePrevisao } from './previsaoDetalhePolicy';
import { motivoNaoVolta } from './voltaParaPlanosPolicy';
import ModalVoltaParaPlanos from './ModalVoltaParaPlanos';
import { useSetFullBreadcrumbTrail } from '../../../../context/BreadcrumbContext';
import moment from 'moment';
import '../../../../css/financeiro-parcela-pagar.css';

// Tela de DETALHE de uma previsão de pagamento (um plano/OPF do Custo Previsto — D-0097, Rafael
// 24/10). Abre pelo clique na linha ou pelo ⋮ → Detalhes das abas Planos de Pagamento e Aprovados
// pelo Financeiro. Lê a MESMA linha da lista (/orcamentoPagamentoCustoPrevisto?opf_id=) — o detalhe
// mostra pelo menos o que a linha mostra — mais os itens do fornecedor ("Ver itens", D-0026) e a
// linha do tempo do plano. Sem Pagar; a única ação é a volta do aprovado para a 1ª aba.
export default function DetalhePrevisaoPagamento() {
    const { id } = useParams();
    const history = useHistory();
    const token = localStorage.getItem('token');

    const [plano, setPlano] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [itens, setItens] = useState(null); // null = carregando ou indisponível
    const [historico, setHistorico] = useState([]);
    const [voltando, setVoltando] = useState(false);

    const carregar = useCallback(() => {
        setCarregando(true);
        api({ method: 'get', action: '/orcamentoPagamentoCustoPrevisto', token: token, params: { opf_id: id } })
            .then((response) => {
                const linha = ((response.data && response.data.data) || [])[0] || null;
                if (!linha) {
                    setErro('Esta previsão não está mais no Custo Previsto: já foi liberada para Contas Pendentes ou não existe.');
                    setPlano(null);
                    return;
                }
                setPlano(linha);
                setErro(null);
                api({ method: 'get', action: '/orcamento/' + linha.orcamento_id + '/pagamentoFornecedores', token: token })
                    .then((r) => setItens(itensDoPlano((r.data && r.data.data) || [], linha)))
                    .catch(() => setItens([]));
            })
            .catch((error) => {
                setErro(error.response?.data?.error ?? error.response?.data?.message ?? 'Erro ao carregar a previsão.');
                setPlano(null);
            })
            .finally(() => setCarregando(false));

        api({ method: 'get', action: '/orcamentoPagamentoFornecedor/' + id + '/historico', token: token })
            .then((response) => setHistorico(response.data?.data ?? []))
            .catch(() => setHistorico([]));
    }, [id, token]);

    useEffect(() => { carregar(); }, [carregar]);

    const setFullTrail = useSetFullBreadcrumbTrail();
    useEffect(() => {
        setFullTrail([
            { path: '/financeiro', label: 'Financeiro' },
            { path: '/financeiroParcelaPagar', label: 'Parcelas a Pagar' },
            { path: rotaDetalhePrevisao(id), label: 'Detalhe da previsão' },
        ]);
    }, [setFullTrail, id]);

    const dataBr = (d) => (d ? moment(d).format('DD/MM/YYYY') : '—');
    const situacao = situacaoPlano(plano);
    const nf = situacaoNfPlano(plano, dataBr);
    const grupos = agruparItensPorGrupo(itens || []);
    const aprovado = plano && plano.status === 'aprovado';
    const naoVolta = aprovado ? motivoNaoVolta(Object.assign({ _tipo: 'previsao' }, plano)) : null;
    const parcelas = (plano && Array.isArray(plano.parcelas)) ? plano.parcelas : [];
    const nfs = (plano && Array.isArray(plano.nfs)) ? plano.nfs : [];
    const pendencias = (plano && Array.isArray(plano.pendencias_pagamento)) ? plano.pendencias_pagamento : [];

    const Linha = ({ label, children }) => (
        <tr>
            <td>{label}</td>
            <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{children}</td>
        </tr>
    );

    const textoTotais = (totais) => Object.entries(totais).map(([m, v]) => formatarValorMoeda(v, m)).join(' + ') || '—';

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Detalhe da previsão de pagamento</h1>
                        <p className="fin-pp__subtitle">
                            {plano ? (plano.fornecedor_nome + ' · Orç. #' + plano.orcamento_codigo) : 'Carregando…'}
                        </p>
                    </div>
                    <div className="fin-pp__actions">
                        <button type="button" className="fin-pp__btn" onClick={() => history.goBack()}>
                            <FaArrowLeft size={15} /> Voltar
                        </button>
                        {aprovado && (
                            <button type="button" className="fin-pp__btn" disabled={!!naoVolta} title={naoVolta || undefined}
                                onClick={() => setVoltando(true)}>
                                <FaSync size={15} /> Voltar para Planos de Pagamento
                            </button>
                        )}
                    </div>
                </div>

                {carregando && (
                    <div className="fin-pp__card">
                        <div className="fin-pp__empty" style={{ padding: '48px 0' }}>Carregando…</div>
                    </div>
                )}

                {!carregando && erro && (
                    <div className="fin-pp__card">
                        <div className="fin-pp__empty" style={{ padding: '40px 0' }}>
                            <FaExclamationTriangle size={26} style={{ color: 'var(--fin-wn)' }} />
                            <div style={{ marginTop: 8 }}>{erro}</div>
                        </div>
                    </div>
                )}

                {!carregando && plano && (<>
                    <section className="fin-pp__kpis" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Total previsto</span>
                                <span className="fin-pp__kpi-ic"><FaMoneyCheckAlt size={16} /></span>
                            </div>
                            <div className="fin-pp__kpi-value">R$ {moeda(plano.total)}</div>
                            <div className="fin-pp__kpi-sub">{parcelas.length === 1 ? '1 parcela' : parcelas.length + ' parcelas'}</div>
                        </div>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Valor no orçamento</span>
                                <span className="fin-pp__kpi-ic"><FaListUl size={16} /></span>
                            </div>
                            <div className={'fin-pp__kpi-value ' + (plano.desatualizado ? 'is-warn' : '')}>R$ {moeda(plano.total_atual_orcamento_brl)}</div>
                            <div className="fin-pp__kpi-sub">
                                {plano.desatualizado ? 'Diverge das parcelas' : (plano.origem_valor ? 'Etapa: ' + plano.origem_valor : '—')}
                            </div>
                        </div>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Situação</span>
                                <span className={'fin-pp__kpi-ic' + (situacao.tom === 'is-ok' ? ' is-ok' : '')}><FaThumbsUp size={16} /></span>
                            </div>
                            <div className={'fin-pp__kpi-value ' + situacao.tom} style={{ fontSize: '1.15rem' }}>{situacao.texto}</div>
                            <div className="fin-pp__kpi-sub">{situacao.sub}</div>
                        </div>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Nota fiscal</span>
                                <span className="fin-pp__kpi-ic"><FaFileInvoice size={16} /></span>
                            </div>
                            <div className={'fin-pp__kpi-value ' + nf.tom} style={{ fontSize: '1.15rem' }}>{nf.texto}</div>
                            <div className="fin-pp__kpi-sub">{nf.sub}</div>
                        </div>
                    </section>

                    {(naoVolta || pendencias.length > 0 || plano.orcamento_alterado) && (
                        <div className="fin-pp__statusbar" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
                            {naoVolta && <span className="fin-pp__pill is-warn" title={naoVolta}>Não volta para Planos: {naoVolta}</span>}
                            {pendencias.length > 0 && <span className="fin-pp__pill is-warn">Dados de pagamento pendentes</span>}
                            {plano.orcamento_alterado && <span className="fin-pp__pill is-info">Orçamento alterado depois da liberação</span>}
                        </div>
                    )}

                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaFileAlt size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">Dados da previsão</h2>
                                <p className="fin-pp__cphead-sub">Fornecedor, cliente, evento e orçamento deste plano</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl fin-pp__tbl--kv">
                                    <tbody>
                                        <Linha label="Fornecedor">
                                            <strong>{plano.fornecedor_nome || '—'}</strong>
                                            {plano.fornecedor_cnpj ? <div className="fin-pp__cell-muted">{plano.fornecedor_cnpj}</div> : null}
                                            {plano.natureza ? <span className="fin-pp__minitag is-info" style={{ marginLeft: 0 }}>{plano.natureza === 'internacional' ? 'Internacional' : 'Nacional'}</span> : null}
                                        </Linha>
                                        <Linha label="Cliente">{plano.cliente_nome || '—'}</Linha>
                                        <Linha label="Evento">{plano.evento_nome || '—'}</Linha>
                                        <Linha label="Orçamento">#{plano.orcamento_codigo}{plano.orcamento_titulo ? ' — ' + plano.orcamento_titulo : ''}</Linha>
                                        <Linha label="Previsão de pagamento">{dataBr(plano.data_previsao_pagamento)}</Linha>
                                        {plano.usar_adiantamento === 'sim' && (
                                            <Linha label="Adiantamento">
                                                R$ {moeda(plano.valor_adiantamento)}
                                                {plano.adiantamento_ja_abatido ? <span className="fin-pp__cell-muted"> · já abatido numa parcela paga</span> : null}
                                            </Linha>
                                        )}
                                        {plano.vai_contas_pagar === false && (<Linha label="Contas a Pagar">Não vai para Contas a Pagar (só faturamento)</Linha>)}
                                        {plano.observacao_aprovacao && (<Linha label="Observação da aprovação">{plano.observacao_aprovacao}</Linha>)}
                                        {plano.motivo_reprovacao && (<Linha label="Motivo da reprovação"><span style={{ color: 'var(--fin-dg)' }}>{plano.motivo_reprovacao}</span></Linha>)}
                                        {pendencias.length > 0 && (
                                            <Linha label="Pendências de pagamento">
                                                {pendencias.map((p, i) => <div key={i}>{p}</div>)}
                                            </Linha>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>

                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaCalendarAlt size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">Parcelas previstas</h2>
                                <p className="fin-pp__cphead-sub">Datas, valores e como pagar (preenchido pelo operador no Efetivo)</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            {parcelas.length === 0 ? (
                                <div className="fin-pp__empty" style={{ padding: '28px 0' }}>Plano sem parcelas — o operador monta na Previsão de Pagamento.</div>
                            ) : (
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                        <thead><tr>
                                            <th style={{ width: 120 }}>Data</th>
                                            <th style={{ width: 160 }}>Forma</th>
                                            <th>Dados de pagamento</th>
                                            <th>Processo antigo</th>
                                            <th style={{ textAlign: 'right', width: 150 }}>Valor</th>
                                        </tr></thead>
                                        <tbody>
                                            {parcelas.map((p) => {
                                                const dados = listaDadosPagamento(p.dados_pagamento);
                                                return (
                                                    <tr key={p.id}>
                                                        <td style={{ whiteSpace: 'nowrap' }}>{dataBr(p.data_pagamento)}</td>
                                                        <td>
                                                            {labelFormaPgto(p.forma) || <span className="fin-pp__cell-muted">—</span>}
                                                            {p.complemento && <span className="fin-pp__minitag is-info" style={{ marginLeft: 6 }}>complemento</span>}
                                                        </td>
                                                        <td className="fin-pp__cell-muted">
                                                            {dados.length === 0 ? 'Não informado no Efetivo' : dados.map(([k, v]) => <div key={k}>{k}: {v}</div>)}
                                                        </td>
                                                        <td>
                                                            <PagamentoLegadoParcela info={p.pagamento_legado}
                                                                onAtualizado={(info) => setPlano((atual) => ({ ...atual,
                                                                    parcelas: (atual.parcelas || []).map((parcelaAtual) =>
                                                                        String(parcelaAtual.id) === String(info.previsao_parcela_id)
                                                                            ? { ...parcelaAtual, pagamento_legado: info } : parcelaAtual),
                                                                }))} />
                                                        </td>
                                                        <td className="fin-pp__cell-num" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>R$ {moeda(p.valor)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="fin-pp__region" style={{ marginBottom: 18 }} data-itens-previsao>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaListUl size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">Itens do fornecedor</h2>
                                <p className="fin-pp__cphead-sub">De que é este pagamento, por grupo do orçamento</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            {itens === null ? (
                                <div className="fin-pp__empty" style={{ padding: '28px 0' }}>Carregando…</div>
                            ) : itens.length === 0 ? (
                                <div className="fin-pp__empty" style={{ padding: '28px 0' }}>Nenhum item do orçamento para este fornecedor.</div>
                            ) : (<>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                        <thead><tr>
                                            <th>Item</th>
                                            <th>Etapa</th>
                                            <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>Valor</th>
                                        </tr></thead>
                                        {grupos.map((g) => (
                                            <tbody key={g.grupo_id} data-itens-grupo={g.grupo_id}>
                                                <tr className="fin-pp__tbl-sub">
                                                    <td colSpan={2} className="fin-pp__cell-strong">{g.grupo_nome}</td>
                                                    <td className="fin-pp__cell-num" style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{textoTotais(g.totais)}</td>
                                                </tr>
                                                {g.itens.map((item) => (
                                                    <tr key={item.id}>
                                                        <td style={{ paddingLeft: 24 }}>
                                                            {item.nome}
                                                            {item.pago_cliente && <span className="fin-pp__pill is-muted ms-2">Pago pelo cliente</span>}
                                                        </td>
                                                        <td className="fin-pp__cell-muted">{item.etapa}</td>
                                                        <td className="fin-pp__cell-num" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatarValorMoeda(item.valor, item.moeda)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        ))}
                                    </table>
                                </div>
                                <div className="fin-pp__cell-muted" style={{ padding: '10px 14px' }}>Valor da última etapa preenchida de cada item, na moeda do item.</div>
                            </>)}
                        </div>
                    </section>

                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaFileInvoice size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">Notas fiscais</h2>
                                <p className="fin-pp__cphead-sub">Notas deste fornecedor no orçamento</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            {nfs.length === 0 ? (
                                <div className="fin-pp__empty" style={{ padding: '28px 0' }}>Nenhuma nota anexada.</div>
                            ) : (
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                        <thead><tr>
                                            <th>Nota</th>
                                            <th style={{ width: 120 }}>Emissão</th>
                                            <th style={{ textAlign: 'right', width: 140 }}>Bruto</th>
                                            <th style={{ textAlign: 'right', width: 140 }}>Líquido</th>
                                            <th style={{ width: 110 }}>Ações</th>
                                        </tr></thead>
                                        <tbody>
                                            {nfs.map((n) => (
                                                <tr key={n.nota_fiscal_id || n.arquivo_id}>
                                                    <td className="fin-pp__cell-strong fin-pp__ellipsis" title={n.nome}>
                                                        {[n.tipo ? String(n.tipo).toUpperCase() : null, n.numero ? 'nº ' + n.numero : null, n.serie ? 'série ' + n.serie : null].filter(Boolean).join(' ') || n.nome || 'Nota'}
                                                    </td>
                                                    <td className="fin-pp__cell-muted">{dataBr(n.data_emissao)}</td>
                                                    <td className="fin-pp__cell-num" style={{ textAlign: 'right' }}>{n.valor_bruto != null ? 'R$ ' + moeda(n.valor_bruto) : '—'}</td>
                                                    <td className="fin-pp__cell-num" style={{ textAlign: 'right' }}>{n.valor_liquido != null ? 'R$ ' + moeda(n.valor_liquido) : '—'}</td>
                                                    <td>
                                                        {n.arquivo_id && (
                                                            <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                                onClick={() => window.open(process.env.REACT_APP_API_URL + '/arquivo/download/' + n.arquivo_id, '_blank')}>
                                                                Abrir
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="fin-pp__region">
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaHistory size={18} /></span>
                            <div>
                                <h2 className="fin-pp__cphead-title">Histórico do plano</h2>
                                <p className="fin-pp__cphead-sub">Voltas do Financeiro (com o motivo) e aprovações revertidas por mudança</p>
                            </div>
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            {historico.length === 0 ? (
                                <div className="fin-pp__empty" style={{ padding: '28px 0' }}>Sem registros ainda.</div>
                            ) : (
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <thead><tr>
                                            <th style={{ width: 150 }}>Quando</th>
                                            <th>O que aconteceu</th>
                                            <th style={{ width: 190 }}>Quem</th>
                                        </tr></thead>
                                        <tbody>
                                            {historico.map((h) => (
                                                <tr key={h.id}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{h.data ? moment(h.data).format('DD/MM/YYYY HH:mm') : '—'}</td>
                                                    <td style={{ whiteSpace: 'pre-wrap' }}>{h.descricao}</td>
                                                    <td className="fin-pp__cell-muted">{h.usuario_nome || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </section>
                </>)}
            </div>

            {voltando && plano && (
                <ModalVoltaParaPlanos
                    plano={{ id: plano.id, fornecedor: plano.fornecedor_nome, referencia: 'Orç. #' + plano.orcamento_codigo }}
                    onFechar={(falhou) => { setVoltando(false); if (falhou) carregar(); }}
                    onConcluido={() => { setVoltando(false); carregar(); }} />
            )}
        </ErpTemplate>
    );
}
